import { CartLinesUpdateEvent } from '@shopify/events';

let freeShippingState = null;
const viewedUpsells = new Set();

class CartFreeShippingProgress extends HTMLElement {
  connectedCallback() {
    const reached = this.dataset.thresholdReached === 'true';

    queueMicrotask(() => {
      const checkoutLabel = this.closest('#cart-drawer')?.querySelector('.cart__checkout-button .button-text');
      if (checkoutLabel) checkoutLabel.textContent = 'Passer au paiement';
    });

    if (freeShippingState === false && reached) {
      document.dispatchEvent(
        new CustomEvent('solivaya:free-shipping-reached', {
          detail: {
            cartTotal: Number(this.dataset.cartTotal || 0),
            threshold: Number(this.dataset.threshold || 0),
          },
        })
      );
    }

    freeShippingState = reached;
  }
}

class CartContextualUpsells extends HTMLElement {
  #observer = null;
  #busy = false;

  connectedCallback() {
    this.#rankCandidates();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleVariantChange);
    this.#observer?.disconnect();
  }

  #rankCandidates() {
    const candidates = Array.from(this.querySelectorAll('[data-upsell-candidate]'));
    if (candidates.length === 0) return;

    const compare = (a, b) => {
      const priorityDifference = Number(a.dataset.priority || 0) - Number(b.dataset.priority || 0);
      if (priorityDifference !== 0) return priorityDifference;

      const thresholdDifference = Number(b.dataset.reachesThreshold === 'true') - Number(a.dataset.reachesThreshold === 'true');
      if (thresholdDifference !== 0) return thresholdDifference;

      return Number(a.dataset.sourceIndex || 0) - Number(b.dataset.sourceIndex || 0);
    };

    candidates.sort(compare);
    const selected = [candidates[0]];

    candidates.forEach((candidate) => {
      candidate.hidden = !selected.includes(candidate);
    });

    this.#observeVisibleUpsells(selected);
  }

  #observeVisibleUpsells(items) {
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => this.#dispatchView(item));
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.#dispatchView(entry.target);
          this.#observer?.unobserve(entry.target);
        });
      },
      { threshold: 0.55 }
    );

    items.forEach((item) => this.#observer?.observe(item));
  }

  #dispatchView(item) {
    const productId = item.dataset.productId;
    if (!productId || viewedUpsells.has(productId)) return;

    viewedUpsells.add(productId);
    document.dispatchEvent(
      new CustomEvent('solivaya:cart-upsell-view', {
        detail: {
          productId,
          variantId: item.dataset.variantId,
          price: Number(item.dataset.price || 0),
        },
      })
    );
  }

  #handleClick = (event) => {
    const button = event.target.closest('[data-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || this.#busy) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    this.#addUpsell(button, variantId);
  };

  #handleVariantChange = (event) => {
    const select = event.target.closest('[data-upsell-variant-select]');
    if (!(select instanceof HTMLSelectElement) || !this.contains(select)) return;

    const item = select.closest('[data-upsell-candidate]');
    const option = select.selectedOptions[0];
    const button = item?.querySelector('[data-upsell-add]');
    if (!(item instanceof HTMLElement) || !(button instanceof HTMLButtonElement) || !option) return;

    item.dataset.variantId = option.value;
    item.dataset.price = option.dataset.price || '0';
    item.dataset.discountedPrice = option.dataset.discountedPrice || '0';
    button.dataset.variantId = option.value;

    const originalPrice = item.querySelector('[data-upsell-original-price]');
    const discountedPrice = item.querySelector('[data-upsell-discounted-price]');
    if (originalPrice) originalPrice.textContent = option.dataset.priceLabel || '';
    if (discountedPrice) discountedPrice.textContent = option.dataset.discountedPriceLabel || '';

    const cartTotal = Number(this.dataset.cartTotal || 0);
    const threshold = Number(this.dataset.freeShippingThreshold || 0);
    const projectedTotal = cartTotal + Number(option.dataset.discountedPrice || 0);
    const reachesThreshold = threshold > 0 && cartTotal < threshold && projectedTotal >= threshold;
    item.dataset.reachesThreshold = String(reachesThreshold);
    const thresholdMessage = item.querySelector('.cart-upsell-item__threshold');
    if (thresholdMessage instanceof HTMLElement) thresholdMessage.hidden = !reachesThreshold;
  };

  async #addUpsell(button, variantId) {
    this.#busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const errorElement = this.querySelector('.cart-upsells__error');
    if (errorElement instanceof HTMLElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const offerId = this.dataset.offerId?.trim();
    const root = window.Shopify?.routes?.root || '/';
    const upsellToken = `${offerId}-${Date.now()}`;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-upsell',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      if (!offerId) throw new Error("L'offre panier n'est pas disponible pour le moment.");

      const initialCartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!initialCartResponse.ok) throw new Error("Le panier n'a pas pu être vérifié.");
      const initialCart = await initialCartResponse.json();
      const obsoleteOfferCodes = new Set(['RITUEL10', 'RITUEL10UPSELL']);
      const existingDiscountCodes = (initialCart.discount_codes || [])
        .map((discount) => discount.code)
        .filter(
          (code) =>
            code && code.toUpperCase() !== offerId.toUpperCase() && !obsoleteOfferCodes.has(code.toUpperCase())
        );

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: { _solivaya_upsell: offerId, _solivaya_upsell_token: upsellToken },
            },
          ],
        }),
      });
      const responsePayload = await response.json();

      if (!response.ok || responsePayload.status) {
        throw new Error(responsePayload.description || responsePayload.message || "Ce produit n'a pas pu être ajouté.");
      }

      const cartResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          updates: {},
          discount: [...existingDiscountCodes, offerId].join(','),
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!cartResponse.ok) throw new Error("Le panier n'a pas pu être recalculé.");
      let cart = await cartResponse.json();
      const findAddedItem = (cartPayload) =>
        cartPayload.items?.find(
          (item) => item.properties?._solivaya_upsell_token === upsellToken && Number(item.variant_id) === variantId
        );
      const hasDiscount = (item) => {
        if (!item) return false;

        const allocations = [
          ...(Array.isArray(item.discount_allocations) ? item.discount_allocations : []),
          ...(Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : []),
        ];
        const hasAllocation = allocations.some((allocation) => Number(allocation.amount || 0) > 0);
        const hasReducedLinePrice =
          Number(item.original_line_price || 0) > 0 &&
          Number(item.final_line_price || 0) < Number(item.original_line_price || 0);

        return hasAllocation || hasReducedLinePrice;
      };
      const isOfferCodeApplicable = (cartPayload) =>
        Boolean(
          cartPayload.discount_codes?.some(
            (discount) =>
              discount.code?.toUpperCase() === offerId.toUpperCase() && discount.applicable === true
          )
        );
      let addedItem = findAddedItem(cart);
      let discountApplied = hasDiscount(addedItem) || isOfferCodeApplicable(cart);

      if (!discountApplied) {
        await new Promise((resolve) => setTimeout(resolve, 350));
        const refreshedCartResponse = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (refreshedCartResponse.ok) {
          const refreshedCart = await refreshedCartResponse.json();
          addedItem = findAddedItem(refreshedCart);
          discountApplied = hasDiscount(addedItem) || isOfferCodeApplicable(refreshedCart);
          if (discountApplied) cart = refreshedCart;
        }
      }

      if (discountApplied && !cart.sections) {
        const renderedCartResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            updates: {},
            sections: [...new Set(sectionIds)].join(','),
            sections_url: window.location.pathname,
          }),
        });
        if (renderedCartResponse.ok) cart = await renderedCartResponse.json();
      }

      if (!discountApplied) {
        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            updates: { [addedItem?.key || variantId]: 0 },
            discount: existingDiscountCodes.join(','),
          }),
        });
        throw new Error("La remise de 10 % n'a pas pu être appliquée. Aucun produit n'a été ajouté.");
      }

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          productId: button.dataset.productId,
          source: 'cart-upsell',
          discountCode: offerId,
          didError: false,
        },
      });

      document.dispatchEvent(
        new CustomEvent('solivaya:cart-upsell-add', {
          detail: {
            productId: button.dataset.productId,
            variantId: String(variantId),
            cartTotal: cart.total_price,
            itemCount: cart.item_count,
          },
        })
      );
    } catch (error) {
      deferredEventPromise.reject(error);
      if (errorElement instanceof HTMLElement && this.isConnected) {
        errorElement.textContent = error instanceof Error ? error.message : "Ce produit n'a pas pu être ajouté.";
        errorElement.hidden = false;
      }
      if (this.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('cart-free-shipping-progress')) {
  customElements.define('cart-free-shipping-progress', CartFreeShippingProgress);
}

if (!customElements.get('cart-contextual-upsells')) {
  customElements.define('cart-contextual-upsells', CartContextualUpsells);
}
